import { Component, Input, OnInit, ElementRef, EventEmitter, Output } from '@angular/core';

export interface ShareConversationSharePayload {
  conversationId: string;
  emails: string[];
  mailContent: string;
  subject: string;
}

export interface ShareConversationData {
  conversationId: string;
  conversationTitle: string;
}

@Component({
  selector: 'app-share-conversation-modal',
  templateUrl: './share-conversation-modal.component.html',
  styleUrls: ['./share-conversation-modal.component.css']
})

export class ShareConversationModalComponent implements OnInit {
  private lastSubject: string = '';
  private lastContent: string = '';
  onMailContentKeydown(event: KeyboardEvent) {
    if (event.ctrlKey) {
      if (event.key === 'b' || event.key === 'B') {
        event.preventDefault();
        this.applyFormat('bold');
      } else if (event.key === 'i' || event.key === 'I') {
        event.preventDefault();
        this.applyFormat('italic');
      } else if (event.key === 'u' || event.key === 'U') {
        event.preventDefault();
        this.applyFormat('underline');
      }
    }
  }
  formatState = {
    bold: false,
    italic: false,
    underline: false
  };

  updateFormatState() {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const contentDiv = (this.el.nativeElement as HTMLElement).querySelector('#mailContent') as HTMLDivElement;
    if (!contentDiv) return;
    // Only update formatState if selection is inside mailContent
    const anchorNode = sel.anchorNode;
    if (!anchorNode) return;
    let node: Node | null = anchorNode;
    let found = false;
    while (node) {
      if (node === contentDiv) {
        found = true;
        break;
      }
      node = node.parentNode;
    }
    if (!found) return;
    const commands = ['bold', 'italic', 'underline'] as const;
    for (const cmd of commands) {
      this.formatState[cmd] = document.queryCommandState(cmd);
    }
  }
  @Input() shareData: ShareConversationData | null = null;
  @Output() onShare = new EventEmitter<ShareConversationSharePayload>();
  
  isLoading = false;
  emailInput = '';
  emailList: string[] = [];
  emailInputError = '';

  mailContent: string = '';
  subject: string = '';

  constructor(public el: ElementRef) { }
  applyFormat(format: 'bold' | 'italic' | 'underline') {
    const contentDiv = (this.el.nativeElement as HTMLElement).querySelector('#mailContent') as HTMLDivElement;
    if (!contentDiv) return;
    document.execCommand(format);
    this.mailContent = contentDiv.innerHTML;
    this.updateFormatState();
  }

  onMailContentInput(event: Event) {
    const contentDiv = event.target as HTMLDivElement;
    this.mailContent = contentDiv.innerHTML;
    this.updateFormatState();
  }

  ngOnInit(): void {
    ($(this.el.nativeElement).find('#shareConversationModal') as any).on('hidden.bs.modal', () => {
      this.resetModal();
    });
    document.addEventListener('selectionchange', this._boundSelectionChange = this.updateFormatState.bind(this));
  }

  ngOnDestroy(): void {
    if (this._boundSelectionChange) {
      document.removeEventListener('selectionchange', this._boundSelectionChange);
    }
  }

  private _boundSelectionChange?: () => void;

  open(shareData: ShareConversationData) {
    this.shareData = shareData;
    this.resetModal();
    this.setSubjectAndContent(
      'Shared MS AI-Agent Conversation',
      `Hello,<br><br>Please find attached the PDF report containing the conversation conducted with Material Selection AI-Agent. This document provides a detailed record of the queries submitted and the corresponding answers generated by the system. The document attached is intended for reference only.<br><br>Kind regards,`
    );
    ($(this.el.nativeElement).find('#shareConversationModal') as any).modal('show');
  }

  setSubjectAndContent(subject: string, content: string) {
    this.subject = subject;
    this.mailContent = content;
    setTimeout(() => {
      const contentDiv = (this.el.nativeElement as HTMLElement).querySelector('#mailContent') as HTMLDivElement;
      if (contentDiv) contentDiv.innerHTML = this.mailContent;
    });
  }

  close() {
    ($(this.el.nativeElement).find('#shareConversationModal') as any).modal('hide');
  }

  private resetModal() {
    this.isLoading = false;
    this.emailInput = '';
    this.emailList = [];
    this.emailInputError = '';

    this.updateFormatState();
  }

  addEmail() {
    const email = this.emailInput.trim().toLowerCase();
    this.emailInputError = '';

    if (!email) {
      this.emailInputError = 'Please enter an email address';
      return;
    }

    if (!this.isValidEmail(email)) {
      this.emailInputError = 'Please enter a valid email address';
      return;
    }

    if (this.emailList.includes(email)) {
      this.emailInputError = 'This email is already in the list';
      return;
    }

    this.emailList.push(email);
    this.emailInput = '';
  }

  removeEmail(index: number) {
    this.emailList.splice(index, 1);
  }

  onEmailKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addEmail();
    }
  }

  shareConversation() {
    if (this.emailList.length === 0) {
      this.emailInputError = 'Please add at least one email address';
      return;
    }

    if (!this.shareData?.conversationId) {
      return;
    }

    this.lastSubject = this.subject;
    this.lastContent = this.mailContent;

    // mailContent already contains HTML
    this.isLoading = true;
    const htmlMailContent = this.mailContent.replace(/\n/g, '<br>');
    this.onShare.emit({
      conversationId: this.shareData.conversationId,
      emails: [...this.emailList],
      mailContent: htmlMailContent,
      subject: this.subject
    });
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  onShareCompleted() {
    this.isLoading = false;
    this.close();
  }

  onShareError() {
  this.isLoading = false;
  this.setSubjectAndContent(this.lastSubject, this.lastContent);
  }
}